import type { TemplateResult } from 'lit';
import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ReassignFunctionsDialog } from 'src/content/employees/reassign-functions-dialog.js';
import { EntityContent } from 'src/content/entity-content.js';
import { EditWorkSchedulesDialog } from 'src/pages/staffing-page/edit-work-schedules-dialog.js';
import type { TimekeepingPeriod } from 'src/store/selectors';
import { accessProfiles } from 'src/store/selectors';
import type { FunctionViewModel, WorkSchedule } from '@trinnvis/dabih-api-client';
import { EmployeeViewModelGenderEnum } from '@trinnvis/dabih-api-client';
import 'src/utilities/display-alert';
import '../../library/editors/index.js';
import '../../library/editors/components/d-edit-access.js';
import '../../library/fields/index.js';
import '../../library/lists/d-list-section-attachment.js';
import '../../library/lists/d-list-section-employee-functions.js';
import '../../library/lists/d-list-section-tutorials.js';
import '../../library/lists/d-timekeeping-list-section.js';
import './d-select-profession.js';
import { isEmptyOrInvalidString, uuid } from 'src/utilities/text';
import { validateNorwegianIdNumber } from 'norwegian-national-id-validator';
import type { UpdateSectionItem } from '../update-section-item';
import type { EditWorkSchedulesResult } from '../../pages/staffing-page/edit-work-schedules-result';
import type { ListSectionItemInput } from '../../library/lists/listSectionItemInput';
import type { SelectDropdownOption } from '../../library/editors/elements/select-dropdown-option';
import type { CreateEntityInput } from '../../layout/parts/create-entity-input';
import type { EmployeeViewEditItem } from './employee-view-edit-item';
import type { EmployeeView } from './employee-view';
import type { ReassignFunctionsResult } from './reassign-functions-result';
import type { UserForAccess } from '../../library/editors/components/user-for-access';
import type { ListDefinition } from '../list-definition';
import type { SectionField } from '../../library/components/section-field';
import type { ActionInput } from '../../library/elements/action-input';
import { t } from 'src/i18n';
import { getEarliestTimekeepingPeriod, getLatestTimekeepingPeriod } from 'src/utilities/time-balance/time-balance';
import { LocalDate } from 'src/utilities/local-date';
import type { TimekeepingBeforeEmploymentStartWarning } from 'src/utilities/time-balance/types';

@customElement('d-employment-period-warning')
class _DEmploymentPeriodWarning extends LitElement {
  static readonly styles = css`
    :host {
      display: grid;
      grid-template-columns: 24px 1fr;
      column-gap: 10px;
      align-items: start;
      padding: 6px 0;
      margin-bottom: 6px;
      color: var(--alertColor);
      line-height: var(--lineHeightDefault);
    }

    :host:before {
      content: '';
      display: block;
      width: 24px;
      height: 24px;
      margin-top: 2px;
      background: url(/images/alert.svg) 50% 50% / 24px 24px no-repeat;
    }

    .content {
      min-width: 0;
    }

    .primary {
      margin-right: 6px;
      color: var(--alertColor);
      font-weight: 500;
    }

    .secondary {
      color: var(--alertColor);
      font-size: 14px;
      font-weight: 200;
    }

    button {
      appearance: none;
      padding: 0;
      border: none;
      border-bottom: 1px solid currentColor;
      background: transparent;
      color: var(--alertColor);
      font: inherit;
      font-weight: 400;
      cursor: pointer;
    }

    button:hover {
      color: black;
      border-bottom-color: var(--linkColorGray);
    }
  `;

  @property({ type: Object })
  warning?: TimekeepingBeforeEmploymentStartWarning;

  private dateToEditValue(date: Date): string {
    return date.toISOString().substring(0, 10);
  }

  private renderWarningDateAction(
    text: string | undefined,
    date: Date | undefined,
    eventName: 'set-employment-start-date' | 'set-employment-end-date',
  ) {
    if (this.warning === undefined || text === undefined || date === undefined) {
      return html``;
    }
    const value = this.dateToEditValue(date);
    const dateText = LocalDate.fromString(value).toStringForDisplay();
    const [prefix, suffix = ''] = text.split(dateText);
    return html`
      <br />
      ${prefix}<button
        type="button"
        @click=${() =>
          this.dispatchEvent(
            new CustomEvent<{ value: string }>(eventName, {
              bubbles: true,
              composed: true,
              detail: { value },
            }),
          )}
      >
        ${dateText}</button
      >${suffix}
    `;
  }

  render() {
    if (this.warning === undefined) {
      return html``;
    }
    return html`
      <div class="content">
        <span class="primary">${this.warning.headerText}</span>
        <br />
        <span class="secondary">
          ${this.warning.callToAction}
          ${this.renderWarningDateAction(
            this.warning.latestValidStartDateText,
            this.warning.latestValidStartDate,
            'set-employment-start-date',
          )}
          ${this.renderWarningDateAction(
            this.warning.earliestValidEndDateText,
            this.warning.earliestValidEndDate,
            'set-employment-end-date',
          )}
        </span>
      </div>
    `;
  }
}

/**
 *
 *
 *
 *
 *
 */
@customElement('d-employee-view')
export class DEmployeeView extends EntityContent<EmployeeView, EmployeeViewEditItem> {
  static readonly styles = EntityContent.styles;
  genderOptions = [
    { value: EmployeeViewModelGenderEnum.Female.valueOf(), text: t('employees.options.female') },
    { value: EmployeeViewModelGenderEnum.Male.valueOf(), text: t('employees.options.male') },
  ];
  accessLevelsNoAccess = [{ uuid: 'NONE', value: 'Ingen tilgang' }];
  @property({ type: String })
  entityType = 'employees';
  @property({ type: Object })
  user: any = {};
  @property({ type: Boolean })
  singleUserVersion = false;
  @property({ type: Array })
  employees = [];
  @property({ type: Array })
  functions: FunctionViewModel[] = [];

  private get statusOptions() {
    if (this.entityView.isCurrentOwner) {
      return [
        { value: 'ACTIVE', text: t('employees.options.active') },
        { value: 'LEAVE', text: t('employees.options.leave') },
      ];
    }
    return [
      { value: 'ACTIVE', text: t('employees.options.active') },
      { value: 'LEAVE', text: t('employees.options.leave') },
      { value: 'TERMINATED', text: t('employees.options.terminated') },
    ];
  }

  private get noName(): boolean {
    return !!(
      this.editItem &&
      isEmptyOrInvalidString(this.editItem.firstName) &&
      isEmptyOrInvalidString(this.editItem.lastName)
    );
  }

  private get accessLevelOptions(): SelectDropdownOption[] {
    return accessProfiles().map((a) => {
      return { value: a.name, text: a.displayName };
    });
  }

  isCorrection(l: TimekeepingPeriod) {
    return l.type === 'correction' || l.type === 'correctionPlus';
  }

  isAccessible(l: TimekeepingPeriod) {
    return (
      !this.isCorrection(l) &&
      (this.entityView.currentUserHasStaffingAccess || !this.entityView.leavePeriodEditRestriction || !l.confirmed)
    );
  }

  asListSectionItemInput(list: TimekeepingPeriod[]): ListSectionItemInput[] {
    return list.map((l) => ({
      label: l.timeDisplay,
      accessible: this.isAccessible(l),
      rightLabel: l.hoursDisplay,
      updateStatus: 'none',
      clickData: this.isCorrection(l) ? undefined : JSON.stringify(l),
    }));
  }

  async initializeEditItem() {
    this.editItem = {
      accessLevel: this.entityView.accessLevel || 'NONE',
      address: this.entityView.address,
      associationType: this.entityView.associationType,
      email: this.entityView.email,
      expertise: this.entityView.expertise,
      firstName: this.entityView.firstName,
      gender: this.entityView.gender,
      personalIdentifier: this.entityView.personalIdentifier,
      herNumber: this.entityView.herNumber,
      hprNumber: this.entityView.hprNumber,
      lastName: this.entityView.lastName,
      notes: this.entityView.notes,
      phone: this.entityView.phone,
      profession: this.entityView.profession,
      secondaryPhone: this.entityView.secondaryPhone,
      status: this.entityView.status,
      employmentStartDate: this.entityView.employmentStartDate,
      employmentEndDate: this.entityView.employmentEndDate,
      nextOfKin: this.entityView.nextOfKin,
      functionAssignments: [],
    };
  }

  editUser(item: EmployeeViewEditItem): UserForAccess {
    return {
      uuid: this.entityView.uuid,
      email: item.email,
      accessLevel: item.accessLevel,
      accessExpires: '',
      name: this.entityView.name,
      partnerName: '',
    };
  }

  doneDisabled(): boolean {
    // Check if name is invalid
    if (this.noName) {
      return true;
    }

    // Check if personal identifier is invalid (not empty and not valid)
    if (this.editItem?.personalIdentifier) {
      const cleanedValue = this.editItem.personalIdentifier.replace(/[\s.-]/g, '');
      if (cleanedValue && !validateNorwegianIdNumber(cleanedValue)) {
        return true; // Disable the Done button
      }
    }

    // Check if start date is after end date
    if (
      this.editItem?.employmentStartDate &&
      this.editItem?.employmentEndDate &&
      this.editItem.employmentStartDate > this.editItem.employmentEndDate
    ) {
      return true;
    }
    return false;
  }

  protected onFieldSectionChanged(e: CustomEvent<{ value: EmployeeViewEditItem }>) {
    const item = e.detail.value;

    // Clean up personal identifier if it exists
    if (item.personalIdentifier) {
      const cleanedValue = item.personalIdentifier.replace(/[\s.-]/g, '');
      if (cleanedValue !== item.personalIdentifier) {
        this.editItem = { ...item, personalIdentifier: cleanedValue };
        return;
      }
    }

    // Update the edit item
    this.editItem = item;
  }

  protected async saveEditItem(item: EmployeeViewEditItem): Promise<EmployeeViewEditItem | undefined> {
    // Double-check validation before saving
    if (item.personalIdentifier) {
      const cleanedValue = item.personalIdentifier.replace(/[\s.-]/g, '');
      if (cleanedValue && !validateNorwegianIdNumber(cleanedValue)) {
        alert(t('employees.personalIdentifierMustBe11Digits'));
        return undefined; // Prevent saving
      }
    }
    if (item.employmentStartDate && item.employmentEndDate && item.employmentStartDate > item.employmentEndDate) {
      alert(t('employees.startDateMustBeBeforeEndDate'));
      return undefined;
    }
    return item;
  }

  renderViewMode() {
    return html` ${this.renderFieldSection('employees', this.viewFields(), this.entityView)} `;
  }

  renderEditAccess(item: EmployeeViewEditItem): TemplateResult<1> {
    return html`<d-edit-access
      style="margin-top: 0; border: none; box-shadow: none;"
      class="fullWidth"
      .isSelf=${this.entityView.isCurrentUser}
      .isOwner=${this.entityView.isCurrentOwner}
      .originalEmail=${this.entityView.email}
      .emailValue=${item.email}
      field="contacts_accessLevel"
      .value=${item.accessLevel}
      .editUser=${this.editUser(item)}
      .otherUserEmails=${this.entityView.otherUserEmails}
      @value-changed=${(e: CustomEvent<{ accessLevel: 'NONE' | 'USER'; email: string }>) => {
        this.editItem = { ...item, accessLevel: e.detail.accessLevel, email: e.detail.email };
      }}
    >
    </d-edit-access>`;
  }

  renderEditProfession(item: EmployeeViewEditItem): TemplateResult<1> {
    return html`<d-select-profession
      class="minWidth300"
      .professions=${this.entityView.professions}
      speciality="${item.expertise}"
      .value=${item.profession}
      @value-changed=${(e: CustomEvent<{ value: string; speciality: string }>) => {
        let associationType = item.associationType;
        if (e.detail.value === 'LIS 1') {
          associationType = 'LIS 1';
        }
        this.editItem = {
          ...item,
          profession: e.detail.value,
          expertise: e.detail.speciality,
          associationType,
        };
      }}
    >
    </d-select-profession> `;
  }

  renderEditStatus(item: EmployeeViewEditItem): TemplateResult<1> {
    return html`
      <d-select-dropdown
        class="minWidth200"
        field="employees_status"
        .options=${this.statusOptions}
        .value=${item.status}
        @value-changed=${(e: CustomEvent<{ value: string }>) => {
          this.onStatusChanged(e, item);
        }}
      >
      </d-select-dropdown>
    `;
  }

  renderEditItem(item: EmployeeViewEditItem): TemplateResult<1> {
    return html` ${this.renderFieldSection('employees', this.editFields(item), item)} `;
  }

  private setEmploymentStartDate(value: string) {
    if (this.editItem === undefined) {
      return;
    }
    this.editItem = {
      ...this.editItem,
      employmentStartDate: value,
    };
  }

  private setEmploymentEndDate(value: string) {
    if (this.editItem === undefined) {
      return;
    }
    this.editItem = {
      ...this.editItem,
      employmentEndDate: value,
    };
  }

  renderEmploymentPeriodWarning(): TemplateResult<1> {
    const warning = this.entityView.timekeepingBeforeEmploymentStartWarning;
    if (!warning?.affectedEmployees.includes(this.entityView.uuid)) {
      return html``;
    }
    return html`
      <d-employment-period-warning
        class="fullWidth"
        .warning=${warning}
        @set-employment-start-date=${(e: CustomEvent<{ value: string }>) => this.setEmploymentStartDate(e.detail.value)}
        @set-employment-end-date=${(e: CustomEvent<{ value: string }>) => this.setEmploymentEndDate(e.detail.value)}
      ></d-employment-period-warning>
    `;
  }

  renderFunctions() {
    return html`
      <d-list-section-employee-functions
        .assignedFunctions=${this.entityView.assignedFunctions}
        .unassignedFunctions=${this.entityView.unassignedFunctions}
        .employeeStatus=${this.entityView.status}
        .writeAccess=${this.entityView.currentUserHasWriteAccess}
        .contentStickyTop=${this.contentStickyTop}
        @assign-function=${this.onAssignFunction}
        @create-responsibility=${this.onCreateResponsibility}
      ></d-list-section-employee-functions>
    `;
  }

  private get clampedDefaultDate(): string {
    const today = this.entityView.today.toString();
    if (this.entityView.employmentEndDate && today > this.entityView.employmentEndDate) {
      return this.entityView.employmentEndDate;
    }
    if (this.entityView.employmentStartDate && today < this.entityView.employmentStartDate) {
      return this.entityView.employmentStartDate;
    }
    return today;
  }

  renderTimekeeping() {
    if (this.entityView.timekeepingEmployee) {
      return html`
        <d-timekeeping-list-section
          .defaultDate=${this.clampedDefaultDate}
          .employeeUuid=${this.entityView.uuid}
          .employee=${this.entityView.timekeepingEmployee}
          .periods=${this.asListSectionItemInput(this.entityView.timekeepingPeriods)}
          .saldo=${this.entityView.timekeepingBalance}
          ?currentUserHasAccess=${this.entityView.currentUserHasAccess}
          .leavePeriodEditRestriction=${this.entityView.leavePeriodEditRestriction}
          .currentUserHasAccess=${this.entityView.currentUserHasStaffingAccess}
          .timekeepingBeforeEmploymentStartWarning=${this.entityView.timekeepingBeforeEmploymentStartWarning}
          .contentStickyTop=${this.contentStickyTop}
        ></d-timekeeping-list-section>
      `;
    }
    return html``;
  }

  renderTutorials() {
    if (this.entityView.tutorials.length > 0) {
      return html`
        <d-list-section
          label=${t('employees.courses')}
          icon="contracts"
          .items=${this.entityView.tutorials}
          .writeAccess=${this.entityView.currentUserHasWriteAccess}
          .contentStickyTop=${this.contentStickyTop}
          @item-clicked=${this.tutorialClicked}
        ></d-list-section>
      `;
    }
    return html``;
  }

  protected asUpdateSectionItem(): UpdateSectionItem | undefined {
    return undefined;
  }

  protected viewFields(): SectionField[] {
    const m = this.entityView.featureStates;
    return [
      {
        field: 'profession',
        type: 'view-text',
        class: 'minWidth200',
        condition: m.entityEmployeesComplete,
      },
      {
        field: 'associationType',
        type: 'view-text',
        class: 'minWidth200',
        condition: m.entityEmployeesComplete,
      },
      {
        field: 'accessLevel',
        options: this.accessLevelOptions,
        type: 'view-text',
        class: 'minWidth200',
        condition: !this.singleUserVersion,
      },
      {
        field: 'hprNumber',
        type: 'view-text',
        class: 'minWidth200',
        condition:
          this.entityView.sector === 'HEALTH' && this.entityView.hprNumber !== undefined && m.entityEmployeesComplete,
      },
      {
        field: 'herNumber',
        type: 'view-text',
        class: 'minWidth200',
        condition: this.entityView.sector === 'HEALTH' && m.entityEmployeesComplete,
      },
      {
        field: 'status',
        options: this.statusOptions,
        type: 'view-text',
        class: 'minWidth200',
        condition: !this.singleUserVersion,
      },
      {
        field: 'employmentStartDate',
        type: 'view-date',
        condition: !this.singleUserVersion,
      },
      {
        field: 'employmentEndDate',
        type: 'view-date',
        hideIfEmpty: true,
        condition: !this.singleUserVersion,
      },
      {
        field: 'phone',
        type: 'view-text',
        class: 'minWidth200',
      },
      {
        field: 'secondaryPhone',
        type: 'view-text',
        class: 'minWidth200',
        condition: m.entityEmployeesComplete,
      },
      {
        field: 'email',
        type: 'view-text',
        class: 'minWidth200',
      },
      {
        field: 'personalIdentifier',
        label: t('employees.nationalIdNumber'),
        type: 'view-text',
        class: 'minWidth200',
        condition: m.entityEmployeesComplete || m.meetings,
      },
      {
        field: 'address',
        type: 'view-text',
        class: 'fullWidth',
        condition: m.entityEmployeesComplete,
      },
      {
        field: 'gender',
        type: 'view-text',
        class: 'minWidth200',
        options: this.genderOptions,
        condition: !this.singleUserVersion && m.entityEmployeesComplete,
      },
      {
        field: 'nextOfKin',
        type: 'view-text',
        class: 'fullWidth',
        condition: m.entityEmployeesComplete,
      },
      {
        field: 'notes',
        type: 'view-text',
        class: 'fullWidth',
        condition: m.entityEmployeesComplete,
      },
      {
        field: 'workHoursDescription',
        action: this.entityView.currentUserHasStaffingAccess
          ? { name: t('employees.edit'), action: 'editWorkSchedules' }
          : undefined,
        type: 'view-text',
        class: 'fullWidth',
        condition: m.core || m.staffing,
      },
    ];
  }

  protected lists(): ListDefinition[] {
    let contractActions: ActionInput[] = [];
    let competenceActions: ActionInput[] = [];
    if (this.entityView.currentUserHasWriteAccess) {
      contractActions = [
        {
          name: t('employees.add'),
          action: 'createContract',
          slot: 'top-right',
        },
      ];
      competenceActions = [
        {
          name: t('competence.addTraining'),
          action: 'createCompetenceTraining',
          slot: 'top-right',
        },
        {
          name: t('competence.addCertificate'),
          action: 'createCompetenceCertificate',
          slot: 'top-right',
        },
      ];
    }
    return [
      {
        field: 'organization_functions',
        type: 'custom',
        icon: 'functions',
        items: 'assignedFunctions',
        render: () => this.renderFunctions(),
        condition:
          (this.entityView.featureStates.core || this.entityView.featureStates.hse) &&
          this.entityView.status !== 'TERMINATED',
      },
      {
        field: 'organization_contracts',
        icon: 'contracts',
        actions: contractActions,
        items: 'contracts',
        condition: this.entityView.featureStates.core || this.entityView.featureStates.hse,
      },
      {
        field: 'organization_competence',
        icon: 'tutorials',
        actions: competenceActions,
        items: 'competence',
        condition: this.entityView.featureStates.hseProduct,
      },
      {
        field: '',
        type: 'custom',
        icon: '',
        items: '',
        render: () => this.renderTimekeeping(),
        condition:
          this.entityView.timekeepingEmployee &&
          (this.entityView.isCurrentUser || this.entityView.currentUserHasStaffingAccess) &&
          (this.entityView.featureStates.staffing || this.entityView.featureStates.core),
      },
      {
        field: '',
        type: 'custom',
        icon: '',
        items: '',
        render: () => this.renderTutorials(),
        condition: this.entityView.tutorials.length > 0,
      },
      {
        field: 'generalFields_attachments',
        type: 'custom',
        icon: 'attachments',
        items: 'attachments',
        render: () => this.renderAttachments(),
      },
    ];
  }

  protected createContract() {
    const id = uuid();
    const input: CreateEntityInput = {
      entityType: 'contracts',
      entityUuid: id,
      targetUrl: this.entityView.href + '/contracts/' + id + '?edit',
      pageId: 63,
      employeeId: this.entityView.uuid,
    };
    this.dispatchEvent(
      new CustomEvent<CreateEntityInput>('create-entity', {
        bubbles: true,
        composed: true,
        detail: input,
      }),
    );
  }

  protected createCompetenceTraining() {
    this.createCompetence('COMPLETED_TRAINING');
  }

  protected createCompetenceCertificate() {
    this.createCompetence('CERTIFICATE');
  }

  private createCompetence(competenceType: 'COMPLETED_TRAINING' | 'CERTIFICATE') {
    const id = uuid();
    const input: CreateEntityInput = {
      entityType: 'competence',
      entityUuid: id,
      targetUrl: this.entityView.href + '/competence/' + id + '?edit',
      pageId: 63,
      employeeId: this.entityView.uuid,
      competenceType,
    };
    this.dispatchEvent(
      new CustomEvent<CreateEntityInput>('create-entity', {
        bubbles: true,
        composed: true,
        detail: input,
      }),
    );
  }

  private editFields(item): SectionField[] {
    const m = this.entityView.featureStates;
    const earliestTimekeepingPeriod = getEarliestTimekeepingPeriod(this.entityView.timekeepingPeriods);
    const latestAllowedEmploymentStartDate =
      earliestTimekeepingPeriod === undefined ? '' : LocalDate.fromString(earliestTimekeepingPeriod.start).toString();
    const latestTimekeepingPeriod = getLatestTimekeepingPeriod(this.entityView.timekeepingPeriods);
    const earliestAllowedEmploymentEndDate =
      latestTimekeepingPeriod === undefined ? '' : LocalDate.fromString(latestTimekeepingPeriod.end).toString();
    return [
      {
        field: 'firstName',
        type: 'edit-text',
        placeholder: this.noName ? t('employees.personMustHaveName') : '',
        markIfEmpty: this.noName,
        class: 'minWidth200',
      },
      {
        field: 'lastName',
        type: 'edit-text',
        placeholder: this.noName ? t('employees.personMustHaveName') : '',
        markIfEmpty: this.noName,
        class: 'minWidth200',
      },
      {
        field: 'gender',
        type: 'edit-tags',
        options: this.genderOptions,
        single: true,
        storedAsSingle: true,
        condition: m.entityEmployeesComplete,
      },
      {
        type: 'custom',
        render: () => this.renderEditAccess(item),
        condition: !this.singleUserVersion,
      },
      {
        field: 'phone',
        type: 'edit-text',
        class: 'minWidth200',
      },
      {
        field: 'secondaryPhone',
        type: 'edit-text',
        class: 'minWidth200',
        condition: m.entityEmployeesComplete,
      },
      {
        type: 'custom',
        render: () => this.renderEditProfession(item),
        condition: m.entityEmployeesComplete,
      },
      {
        field: 'associationType',
        type: 'edit-dropdown',
        class: 'minWidth300',
        placeholder: t('employees.select'),
        options: this.entityView.associationTypes.map((associationType) => ({
          value: associationType,
          text: associationType,
        })),
        condition: m.entityEmployeesComplete,
      },
      {
        type: 'custom',
        render: () => this.renderEditStatus(item),
        condition: !this.singleUserVersion,
      },
      {
        type: 'custom',
        render: () => this.renderEmploymentPeriodWarning(),
        condition: !this.singleUserVersion,
      },
      {
        field: 'employmentStartDate',
        type: 'edit-date',
        class: 'minWidth200',
        markIfEmpty: true,
        condition: !this.singleUserVersion,
        disableAfter: latestAllowedEmploymentStartDate,
        underLabel:
          item.employmentStartDate && item.employmentEndDate && item.employmentStartDate > item.employmentEndDate
            ? '<span style="color: var(--alertColor)">' + t('employees.startDateMustBeBeforeEndDate') + '</span>'
            : undefined,
      },
      {
        field: 'employmentEndDate',
        type: 'edit-date',
        class: 'minWidth200',
        clear: true,
        condition: !this.singleUserVersion,
        disableBefore: earliestAllowedEmploymentEndDate,
        underLabel:
          item.employmentStartDate && item.employmentEndDate && item.employmentStartDate > item.employmentEndDate
            ? '<span style="color: var(--alertColor)">' + t('employees.endDateMustBeAfterStartDate') + '</span>'
            : undefined,
      },
      {
        field: 'hprNumber',
        type: 'edit-text',
        class: 'minWidth200',
        condition: this.entityView.sector === 'HEALTH' && m.entityEmployeesComplete,
      },
      {
        field: 'herNumber',
        type: 'edit-text',
        class: 'minWidth200',
        condition: this.entityView.sector === 'HEALTH' && m.entityEmployeesComplete,
      },
      {
        field: 'personalIdentifier',
        label: t('employees.nationalIdNumber'),
        type: 'edit-text',
        class: 'minWidth200',
        condition: m.entityEmployeesComplete || m.meetings,
        placeholder: t('employees.elevenDigitsOptional'),
        underLabel:
          this.editItem?.personalIdentifier && !validateNorwegianIdNumber(this.editItem.personalIdentifier)
            ? '<span style="color: var(--alertColor)">' + t('employees.personalIdentifierMustBe11Digits') + '</span>'
            : undefined,
      },
      {
        field: 'address',
        type: 'edit-textarea',
        class: 'fullWidth',
        condition: m.entityEmployeesComplete,
      },
      {
        field: 'nextOfKin',
        type: 'edit-textarea',
        class: 'fullWidth',
        condition: m.entityEmployeesComplete,
      },
      {
        field: 'notes',
        type: 'edit-textarea',
        class: 'fullWidth',
        condition: m.entityEmployeesComplete,
      },
    ];
  }

  private async onStatusChanged(evt: CustomEvent<{ value: string }>, item: EmployeeViewEditItem) {
    const selectedStatus = evt.detail.value;
    const currentEditItem = item;
    if (currentEditItem && selectedStatus !== currentEditItem.status) {
      // Only show reassignment dialog if functions feature is enabled (core or hse)
      const hasFunctionsFeature = this.entityView.featureStates.core || this.entityView.featureStates.hse;
      if (selectedStatus !== 'ACTIVE' && hasFunctionsFeature && this.entityView.functionsForReassignment.length > 0) {
        await this.reassignForStatusNotActive(currentEditItem, selectedStatus);
      } else {
        this.editItem = {
          ...currentEditItem,
          status: selectedStatus,
          functionAssignments: [],
          accessLevel: selectedStatus === 'TERMINATED' ? 'NONE' : currentEditItem.accessLevel,
          employmentEndDate:
            selectedStatus === 'TERMINATED' && !currentEditItem.employmentEndDate
              ? this.entityView.today.toString()
              : currentEditItem.employmentEndDate,
        };
      }
    }
  }

  private async reassignForStatusNotActive(currentEditItem: EmployeeViewEditItem, selectedStatus: string) {
    const result: ReassignFunctionsResult = await ReassignFunctionsDialog.open({
      employees: this.entityView.employeesForReassignment,
      functions: this.entityView.functionsForReassignment,
      inactiveEmployeeId: this.entityView.uuid,
    });
    if (result.action === 'confirm') {
      this.editItem = {
        ...currentEditItem,
        functionAssignments: result.assignments,
        status: selectedStatus,
        accessLevel: selectedStatus === 'TERMINATED' ? 'NONE' : currentEditItem.accessLevel,
        employmentEndDate:
          selectedStatus === 'TERMINATED' && !currentEditItem.employmentEndDate
            ? this.entityView.today.toString()
            : currentEditItem.employmentEndDate,
      };
    } else {
      const previousStatus = currentEditItem.status;
      this.editItem = {
        ...currentEditItem,
        status: selectedStatus,
      };
      setTimeout(() => {
        this.editItem = {
          ...currentEditItem,
          status: previousStatus ?? 'ACTIVE',
        };
        this.requestUpdate();
      }, 0);
    }
  }

  private async editWorkSchedules() {
    if (!this.entityView.forStaffing) {
      console.warn('Cannot edit work schedules: employee data not loaded');
      return;
    }

    const result: EditWorkSchedulesResult = await EditWorkSchedulesDialog.open({
      employee: this.entityView.forStaffing,
      defaultDate: this.clampedDefaultDate,
    });
    if (result.action === 'done') {
      this.dispatchEvent(
        new CustomEvent<{
          employeeUuid: string;
          workSchedules: WorkSchedule[];
        }>('update-employee-work-schedules', {
          bubbles: true,
          composed: true,
          detail: {
            employeeUuid: this.entityView.uuid,
            workSchedules: result.workSchedules,
          },
        }),
      );
    }
  }

  private tutorialClicked(e: CustomEvent<ListSectionItemInput>) {
    e.preventDefault();
    if (e.detail.clickData !== undefined) {
      this.dispatchEvent(
        new CustomEvent<{ value: string }>('update-tutorial', {
          composed: true,
          bubbles: true,
          detail: { value: e.detail.clickData },
        }),
      );
    }
  }

  private onAssignFunction(e: CustomEvent<{ functionUuid: string }>) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent<{ functionUuid: string; employeeUuid: string }>('assign-function', {
        bubbles: true,
        composed: true,
        detail: {
          functionUuid: e.detail.functionUuid,
          employeeUuid: this.entityView.uuid,
        },
      }),
    );
  }

  private onCreateResponsibility(e: CustomEvent) {
    e.stopPropagation();
    const id = uuid();
    const input: CreateEntityInput = {
      entityType: 'functions',
      entityUuid: id,
      targetUrl: this.entityView.href + '/functions/' + id + '?edit',
      employeeId: this.entityView.uuid,
      pageId: 65,
    };
    this.dispatchEvent(
      new CustomEvent<CreateEntityInput>('create-entity', {
        bubbles: true,
        composed: true,
        detail: input,
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-employee-view': DEmployeeView;
  }
}
